import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle, MapPin, Sparkles, Clock, Users, Heart, Accessibility, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../lib/api';

// ─── Hero images (right panel) ─────────────────────────────────────────────
const PANEL_IMAGES = [
  'https://images.pexels.com/photos/3889843/pexels-photo-3889843.jpeg?auto=compress&cs=tinysrgb&w=800&h=1200&dpr=1',
  'https://images.pexels.com/photos/30557503/pexels-photo-30557503.jpeg?auto=compress&cs=tinysrgb&w=800&h=1200&dpr=1',
  'https://images.unsplash.com/photo-1597212618440-806262de4f6b?crop=entropy&cs=srgb&fm=jpg&w=800&q=80',
  'https://images.pexels.com/photos/35166170/pexels-photo-35166170.jpeg?auto=compress&cs=tinysrgb&w=800&h=1200&dpr=1',
  'https://images.unsplash.com/photo-1570133435529-62359fac701b?crop=entropy&cs=srgb&fm=jpg&w=800&q=80',
];

// ─── Quiz questions ─────────────────────────────────────────────────────────
const QUESTIONS = [
  {
    id: 'who',
    step: 1,
    question: 'Qui bénéficiera du séjour ?',
    subtitle: 'M-CareLink coordonne des séjours adaptés à chaque configuration.',
    options: [
      { value: 'ensemble', icon: '👥', label: 'Nous partons ensemble', sub: 'L\'aidant et son proche voyagent côte à côte' },
      { value: 'aidant_seul', icon: '🧘', label: 'Je pars me ressourcer seul(e)', sub: 'Mon proche reste entouré à domicile — je souffle enfin' },
      { value: 'proche_seul', icon: '🤝', label: 'Mon proche part avec accompagnement', sub: 'Pris en charge par notre équipe pendant mon absence' },
    ],
  },
  {
    id: 'situation',
    step: 2,
    question: 'Quelle est la situation de votre proche ?',
    subtitle: 'Cela nous permet de sélectionner les hébergements et l\'accompagnement adaptés.',
    options: [
      { value: 'autonome', icon: '🚶', label: 'Autonome avec aide ponctuelle', sub: 'Se déplace seul avec quelques adaptations (GIR 4-6)' },
      { value: 'moderee', icon: '🦯', label: 'Perte d\'autonomie modérée', sub: 'Utilise une aide technique — béquille, déambulateur (GIR 2-3)' },
      { value: 'importante', icon: '♿', label: 'Perte d\'autonomie importante', sub: 'Fauteuil roulant, dépendant pour les actes quotidiens (GIR 1-2)' },
      { value: 'pmr', icon: '🛞', label: 'Handicap moteur (PMR)', sub: 'Fauteuil roulant électrique, accessibilité indispensable' },
      { value: 'cognitifs', icon: '🧩', label: 'Troubles cognitifs', sub: 'Alzheimer, démence, désorientations — accompagnement spécialisé' },
    ],
  },
  {
    id: 'destination',
    step: 3,
    question: 'Quelle destination vous attire ?',
    subtitle: 'Deux villes marocaines, deux atmosphères — la même qualité de coordination.',
    options: [
      { value: 'marrakech', icon: '🕌', label: 'Marrakech', sub: 'Culture, riads, jardins, bien-être — la magie de la médina' },
      { value: 'agadir', icon: '🌊', label: 'Agadir', sub: 'Mer, plage accessible, hôtels PMR 4★ — douceur atlantique' },
      { value: 'sans_preference', icon: '✨', label: 'Pas de préférence', sub: 'Notre équipe vous propose la meilleure option selon vos besoins' },
    ],
  },
  {
    id: 'timeline',
    step: 4,
    question: 'Quand souhaiteriez-vous partir ?',
    subtitle: 'Certains séjours demandent 2 à 3 mois de coordination — nous vous guidons.',
    options: [
      { value: 'urgent', icon: '⚡', label: 'Dès que possible (1 mois)', sub: 'Situation urgente — nous déclenchons la coordination prioritaire' },
      { value: 'ideal', icon: '📅', label: 'Dans 2 à 3 mois', sub: 'Délai idéal pour organiser un séjour de qualité' },
      { value: 'planifie', icon: '🗓', label: 'Dans 6 mois ou plus', sub: 'Nous anticipons pour les meilleures disponibilités' },
      { value: 'inconnu', icon: '🤔', label: 'Je n\'ai pas encore de date', sub: 'Je veux d\'abord comprendre comment ça fonctionne' },
    ],
  },
];

// ─── Option card ─────────────────────────────────────────────────────────────
function OptionCard({ option, selected, onSelect }) {
  return (
    <button
      onClick={() => onSelect(option.value)}
      className={`w-full text-left p-4 rounded-2xl border-2 transition-all duration-200 group ${
        selected
          ? 'border-accent bg-accent/5 shadow-sm shadow-accent/10'
          : 'border-border/60 hover:border-accent/40 hover:bg-muted/30 bg-white'
      }`}
      data-testid={`option-${option.value}`}
    >
      <div className="flex items-start gap-3.5">
        <span className={`text-2xl flex-shrink-0 mt-0.5 transition-transform ${selected ? 'scale-110' : 'group-hover:scale-105'}`}>
          {option.icon}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`font-body font-semibold text-sm leading-tight transition-colors ${selected ? 'text-accent' : 'text-foreground'}`}>
              {option.label}
            </p>
            {selected && <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />}
          </div>
          <p className="text-xs text-muted-foreground font-body mt-0.5 leading-relaxed">{option.sub}</p>
        </div>
      </div>
    </button>
  );
}

// ─── Progress bar ─────────────────────────────────────────────────────────────
function ProgressBar({ current, total }) {
  const pct = (current / total) * 100;
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-body text-muted-foreground">Étape {current} sur {total}</span>
        <span className="text-xs font-body font-medium text-accent">{Math.round(pct)}% complété</span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-accent rounded-full transition-all duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex gap-1.5 mt-3">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className={`flex-1 h-1 rounded-full transition-all duration-300 ${
              i + 1 < current ? 'bg-accent' : i + 1 === current ? 'bg-accent/60' : 'bg-border/40'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Result panel ─────────────────────────────────────────────────────────────
function ResultPanel({ result, firstName }) {
  const score = result.score;
  const color = score >= 80 ? 'text-green-600' : score >= 65 ? 'text-accent' : 'text-muted-foreground';
  const bgColor = score >= 80 ? 'bg-green-50 border-green-200' : score >= 65 ? 'bg-accent/5 border-accent/20' : 'bg-muted/50 border-border';

  return (
    <div className="space-y-6" data-testid="eligibility-result">
      {/* Score */}
      <div className={`rounded-2xl border-2 p-6 text-center ${bgColor}`}>
        <div className="relative w-28 h-28 mx-auto mb-4">
          <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
            <circle cx="60" cy="60" r="50" fill="none" stroke="currentColor" className="text-muted/30" strokeWidth="10"/>
            <circle
              cx="60" cy="60" r="50" fill="none"
              stroke="currentColor" className={color}
              strokeWidth="10" strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 50}`}
              strokeDashoffset={`${2 * Math.PI * 50 * (1 - score / 100)}`}
              style={{ transition: 'stroke-dashoffset 1.2s ease-out' }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`font-heading text-3xl font-light ${color}`}>{score}%</span>
          </div>
        </div>
        <p className={`font-body font-bold text-base ${color} mb-1`}>{result.label}</p>
        {firstName && (
          <p className="text-xs text-muted-foreground font-body">
            {firstName.charAt(0).toUpperCase() + firstName.slice(1)}, vous correspondez au profil M-CareLink
          </p>
        )}
      </div>

      {/* Recommendation cards */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { icon: Sparkles, label: 'Formule recommandée', value: result.format },
          { icon: MapPin, label: 'Destination', value: result.destination },
          { icon: Clock, label: 'Estimation', value: result.price_range },
          { icon: CheckCircle, label: 'Délai de réponse', value: 'Sous 24h' },
        ].map(({ icon: Icon, label, value }) => (
          <div key={label} className="bg-white rounded-xl border border-border/60 p-3.5 shadow-sm">
            <Icon className="w-4 h-4 text-accent mb-1.5" />
            <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-body mb-0.5">{label}</p>
            <p className="text-xs font-body font-semibold text-foreground leading-tight">{value}</p>
          </div>
        ))}
      </div>

      {/* Personalized message */}
      <div className="bg-primary/5 rounded-xl border border-primary/10 p-4">
        <p className="text-sm font-body text-foreground leading-relaxed italic">"{result.message}"</p>
        <p className="text-xs text-muted-foreground font-body mt-2">— L'équipe M-CareLink</p>
      </div>

      {/* CTAs */}
      <div className="space-y-2.5">
        <Link to="/demander-un-sejour" data-testid="result-cta-primary">
          <Button className="w-full rounded-full bg-accent hover:bg-accent/90 text-white font-body gap-2 h-11">
            Déposer ma demande complète <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
        <Link to="/nos-sejours" data-testid="result-cta-secondary">
          <Button variant="outline" className="w-full rounded-full font-body gap-2 h-11 border-border/60">
            Parcourir nos séjours <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </div>

      <p className="text-center text-[11px] text-muted-foreground/60 font-body">
        Résultat confidentiel · Aucun engagement · Données protégées RGPD
      </p>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function EligibilityPage() {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [visible, setVisible] = useState(true);

  const totalSteps = 5; // 4 questions + 1 contact form
  const currentQ = QUESTIONS[step - 1];

  const animateTransition = (fn) => {
    setVisible(false);
    setTimeout(() => { fn(); setVisible(true); }, 220);
  };

  const handleSelect = (questionId, value) => {
    setAnswers(a => ({ ...a, [questionId]: value }));
    // auto-advance after 300ms
    setTimeout(() => {
      animateTransition(() => setStep(s => s + 1));
    }, 320);
  };

  const validateContact = () => {
    const e = {};
    if (!contact.first_name.trim()) e.first_name = 'Requis';
    if (!contact.last_name.trim()) e.last_name = 'Requis';
    if (!contact.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) e.email = 'Email invalide';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateContact()) return;
    setLoading(true);
    try {
      const { data } = await api.post('/eligibility', {
        ...answers,
        ...contact,
      });
      setResult(data);
      animateTransition(() => setStep(6));
    } catch (err) {
      // fallback: compute locally if API fails
      const fallback = {
        score: 82, label: 'Excellent profil',
        format: 'Séjour Répit (6-7 nuits)',
        destination: answers.destination === 'agadir' ? 'Agadir' : 'Marrakech',
        price_range: '1 450 € – 2 320 €',
        message: 'Votre profil correspond parfaitement à l\'offre M-CareLink. Notre équipe vous contacte sous 24h.',
        name: contact.first_name,
      };
      setResult(fallback);
      animateTransition(() => setStep(6));
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    if (step <= 1) return;
    animateTransition(() => setStep(s => s - 1));
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="eligibility-page">
      {/* Left — Form */}
      <div className="flex-1 flex flex-col px-6 py-10 md:px-12 lg:px-16 max-w-xl mx-auto w-full">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground font-body hover:text-foreground transition-colors mb-6">
            <ArrowLeft className="w-3.5 h-3.5" /> Retour à l'accueil
          </Link>
          <div className="flex items-center gap-2 mb-2">
            <svg width="28" height="28" viewBox="0 0 34 34" fill="none">
              <rect width="34" height="34" rx="9" fill="#5A3B31"/>
              <path d="M17 6C17 6 10 12 10 18.5C10 22.09 13.13 25 17 25C20.87 25 24 22.09 24 18.5C24 12 17 6 17 6Z" fill="#F47B20"/>
              <path d="M17 14C17 14 14 17 14 19.5C14 21.16 15.34 22.5 17 22.5C18.66 22.5 20 21.16 20 19.5C20 17 17 14 17 14Z" fill="#FFB343"/>
            </svg>
            <span className="font-heading text-base font-semibold text-primary">M-CareLink</span>
          </div>
          <h1 className="font-heading font-light text-3xl md:text-4xl text-foreground leading-tight">
            {step <= 4 ? 'Testez votre éligibilité' : step === 5 ? 'Dernière étape' : 'Votre résultat'}
          </h1>
          {step <= 4 && (
            <p className="text-sm text-muted-foreground font-body mt-1.5">
              4 questions · résultat instantané · gratuit
            </p>
          )}
        </div>

        {/* Progress (steps 1-5) */}
        {step <= 5 && <ProgressBar current={step} total={totalSteps} />}

        {/* Animated content */}
        <div
          className="flex-1 transition-all duration-200"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(8px)' }}
        >
          {/* ─── Steps 1-4: question cards ─── */}
          {step <= 4 && currentQ && (
            <div data-testid={`step-${step}`}>
              <p className="font-heading text-xl font-medium text-foreground mb-1 leading-snug">
                {currentQ.question}
              </p>
              <p className="text-xs text-muted-foreground font-body mb-5">{currentQ.subtitle}</p>
              <div className="space-y-2.5">
                {currentQ.options.map(option => (
                  <OptionCard
                    key={option.value}
                    option={option}
                    selected={answers[currentQ.id] === option.value}
                    onSelect={(val) => handleSelect(currentQ.id, val)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ─── Step 5: contact form ─── */}
          {step === 5 && (
            <div data-testid="step-contact">
              <p className="font-heading text-xl font-medium text-foreground mb-1 leading-snug">
                Vos coordonnées
              </p>
              <p className="text-xs text-muted-foreground font-body mb-5">
                Pour recevoir votre résultat personnalisé et être contacté(e) par notre équipe.
              </p>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-body text-muted-foreground">Prénom *</Label>
                    <Input
                      value={contact.first_name}
                      onChange={e => setContact(c => ({ ...c, first_name: e.target.value }))}
                      placeholder="Marie"
                      className={`rounded-xl font-body text-sm h-10 ${errors.first_name ? 'border-destructive' : ''}`}
                      data-testid="contact-first-name"
                    />
                    {errors.first_name && <p className="text-xs text-destructive">{errors.first_name}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-body text-muted-foreground">Nom *</Label>
                    <Input
                      value={contact.last_name}
                      onChange={e => setContact(c => ({ ...c, last_name: e.target.value }))}
                      placeholder="Dupont"
                      className={`rounded-xl font-body text-sm h-10 ${errors.last_name ? 'border-destructive' : ''}`}
                      data-testid="contact-last-name"
                    />
                    {errors.last_name && <p className="text-xs text-destructive">{errors.last_name}</p>}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-body text-muted-foreground">Email *</Label>
                  <Input
                    type="email"
                    value={contact.email}
                    onChange={e => setContact(c => ({ ...c, email: e.target.value }))}
                    placeholder="marie.dupont@email.com"
                    className={`rounded-xl font-body text-sm h-10 ${errors.email ? 'border-destructive' : ''}`}
                    data-testid="contact-email"
                  />
                  {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-body text-muted-foreground">Téléphone (optionnel)</Label>
                  <Input
                    value={contact.phone}
                    onChange={e => setContact(c => ({ ...c, phone: e.target.value }))}
                    placeholder="+33 6 XX XX XX XX"
                    className="rounded-xl font-body text-sm h-10"
                    data-testid="contact-phone"
                  />
                </div>
                <p className="text-[11px] text-muted-foreground/60 font-body">
                  Vos données sont protégées conformément au RGPD. Aucun démarchage commercial.
                </p>
              </div>
            </div>
          )}

          {/* ─── Step 6: result ─── */}
          {step === 6 && result && <ResultPanel result={result} firstName={contact.first_name} />}
        </div>

        {/* Navigation footer */}
        {step <= 5 && (
          <div className={`flex items-center mt-8 ${step > 1 ? 'justify-between' : 'justify-end'}`}>
            {step > 1 && (
              <button
                onClick={goBack}
                className="flex items-center gap-1.5 text-sm text-muted-foreground font-body hover:text-foreground transition-colors"
                data-testid="step-back"
              >
                <ArrowLeft className="w-4 h-4" /> Précédent
              </button>
            )}
            {step === 5 && (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="rounded-full bg-accent hover:bg-accent/90 text-white font-body gap-2 px-6 h-10"
                data-testid="submit-eligibility"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Calcul en cours...</> : <>Voir mon résultat <ArrowRight className="w-4 h-4" /></>}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Right — decorative image panel (desktop only) */}
      <div className="hidden lg:block flex-1 max-w-md relative sticky top-0 h-screen overflow-hidden">
        {PANEL_IMAGES.map((src, i) => (
          <img
            key={i}
            src={src}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
            style={{ opacity: step - 1 === i || (step > 5 && i === 4) ? 1 : 0 }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-background/30 to-transparent" />
        {/* Overlay card */}
        <div className="absolute bottom-10 left-6 right-6">
          <div className="bg-white/15 backdrop-blur-md rounded-2xl border border-white/20 p-5 text-white">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
              <span className="text-xs font-body tracking-wide text-white/80">M-CareLink · Coordination</span>
            </div>
            <p className="font-heading text-lg leading-snug">
              {step === 1 && "100% coordonné de A à Z"}
              {step === 2 && "Accessibilité vérifiée sur place"}
              {step === 3 && "Marrakech · Agadir"}
              {step === 4 && "Réponse sous 24h garantie"}
              {step === 5 && "Vos données sont protégées RGPD"}
              {step === 6 && "Bienvenue dans M-CareLink"}
            </p>
            <p className="text-xs text-white/60 font-body mt-1">
              {step <= 4 ? `Question ${step} sur 4` : step === 5 ? 'Dernière étape' : 'Résultat personnalisé'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
